/**
 * usePushNotifications — Chunk 153: Mobile Push Notification Integration
 *
 * Handles the full lifecycle of push notification registration on native
 * Capacitor platforms (iOS/Android) and provides a web-platform stub.
 *
 * Responsibilities:
 *  - Requests OS push permission on mount (native only)
 *  - Captures the FCM/APNs registration token and POSTs it to /push-tokens
 *  - Listens for foreground push notifications
 *  - Navigates on notification tap (pushNotificationActionPerformed):
 *      price_alert / alert_triggered → /watchlist
 *      governance                    → /governance
 *      liquidation / defi_health     → /defi-health
 *      dca                           → /dca
 *      default                       → /dashboard
 *  - Gracefully stubs out when running on web (no-op)
 *
 * Mount this hook inside the authenticated/ProtectedShell context via App.tsx.
 */

import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import {
  PushNotifications,
  type Token,
  type PushNotificationSchema,
  type ActionPerformed,
  type PermissionStatus,
} from '@capacitor/push-notifications';
import { useAppSelector } from '../store/hooks';
import { registerPushToken } from '../api/push-tokens';
import type { PushTokenPlatform } from '../api/push-tokens';

// ─── Navigation helpers ───────────────────────────────────────────────────────

function resolveNotificationRoute(eventType: string): string {
  const t = eventType.toLowerCase();
  if (t.includes('price') || t.includes('alert') || t.includes('watchlist')) {
    return '/watchlist';
  }
  if (t.includes('governance')) {
    return '/governance';
  }
  if (t.includes('liquidation') || t.includes('defi') || t.includes('health')) {
    return '/defi-health';
  }
  if (t.includes('dca')) {
    return '/dca';
  }
  return '/dashboard';
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function usePushNotifications(): void {
  const navigate = useNavigate();
  const user = useAppSelector((state) => state.auth.user);
  const registered = useRef(false);

  useEffect(() => {
    // Only run on native Capacitor platforms (iOS / Android)
    if (!Capacitor.isNativePlatform()) return;
    // Only run once per session and only when authenticated
    if (!user?.id || registered.current) return;

    registered.current = true;

    const platform = Capacitor.getPlatform() as PushTokenPlatform;

    // Keep track of listener handles for cleanup
    const handles: Array<{ remove: () => void }> = [];

    async function setup(): Promise<void> {
      let permStatus: PermissionStatus;

      try {
        permStatus = await PushNotifications.checkPermissions();
      } catch {
        return;
      }

      if (permStatus.receive === 'prompt' || permStatus.receive === 'prompt-with-rationale') {
        try {
          permStatus = await PushNotifications.requestPermissions();
        } catch {
          return;
        }
      }

      if (permStatus.receive !== 'granted') {
        return;
      }

      try {
        await PushNotifications.register();
      } catch {
        return;
      }

      // ─── registration: token received ──────────────────────────────────────
      const registrationHandle = await PushNotifications.addListener(
        'registration',
        async (token: Token) => {
          if (!user?.id) return;
          try {
            await registerPushToken(user.id, {
              token: token.value,
              platform,
              device_label: `${platform} device`,
            });
          } catch {
            // Non-fatal — token registration failure should not surface to user
          }
        }
      );
      handles.push(registrationHandle);

      // ─── registrationError: log only ──────────────────────────────────────
      const errorHandle = await PushNotifications.addListener(
        'registrationError',
        (err: unknown) => {
          console.warn('[usePushNotifications] Registration error:', err);
        }
      );
      handles.push(errorHandle);

      // ─── pushNotificationReceived: foreground notification ─────────────────
      const receivedHandle = await PushNotifications.addListener(
        'pushNotificationReceived',
        (notification: PushNotificationSchema) => {
          // Foreground notifications: show in-app toast/banner
          // (handled by existing NotificationCenter component via WebSocket)
          console.log('[usePushNotifications] Foreground notification:', notification.title);
        }
      );
      handles.push(receivedHandle);

      // ─── pushNotificationActionPerformed: tap-to-open ─────────────────────
      const actionHandle = await PushNotifications.addListener(
        'pushNotificationActionPerformed',
        (action: ActionPerformed) => {
          const eventType: string =
            (action.notification.data as Record<string, string> | undefined)?.['eventType'] ??
            '';
          const actionUrl: string =
            (action.notification.data as Record<string, string> | undefined)?.['actionUrl'] ??
            '';

          const route = actionUrl || resolveNotificationRoute(eventType);
          navigate(route);
        }
      );
      handles.push(actionHandle);
    }

    void setup();

    return () => {
      handles.forEach((h) => h.remove());
      registered.current = false;
    };
  }, [user?.id, navigate]);
}

export default usePushNotifications;
